/* ============================================================
 * ADG Grid Core — paging, auto-load and scroll memory, once.
 *
 * The last fork in the rebuild. Two grid controllers existed —
 * adg-artgrid.js for the design pages and byo-builder.js for the three
 * builders — and between them they owned paging, the Load more policy,
 * scroll memory and the way back. So every improvement to one of those
 * four things landed on exactly one surface, and the other kept the bug.
 * Card markup, filtering, the scroll engine and the lightbox were already
 * shared; this is what was left.
 *
 * WHAT LIVES HERE
 *   - the list currently on show, and how much of it has been drawn
 *   - the adg-infinite.js wiring, its budget, and the Load more fallback
 *   - scroll memory: how many cards to redraw and where to put the shopper
 *
 * WHAT DOES NOT
 *   - what a card looks like and what clicking one does. The design grids
 *     navigate; the builders pick. That is a real difference, so it stays
 *     with the caller as a renderCard function.
 *
 * WHY AN ARRAY AND NOT PAGES — this is the bug fix, not a preference.
 * Both surfaces hold the whole set in memory already (one artwork index
 * fetch). adg-artgrid sliced an array and reported `shown`, an integer that
 * always climbs. byo-builder imitated a server feed — page / hasMore — and
 * reported grid.childElementCount, which a render that appends nothing
 * leaves unchanged. The shared scroll engine reads that as a stall, two
 * stalls and it hands back the button. That is why Load more kept appearing
 * mid-collection on the builders and never on the design pages.
 * Counting an index instead of counting DOM nodes removes the failure mode
 * rather than guarding against it.
 * ============================================================ */
(function (global) {
  'use strict';

  function create(opts) {
    var grid       = opts.grid;
    var sentinel   = opts.sentinel || null;
    var moreBtn    = opts.moreBtn || null;
    var renderCard = opts.renderCard;
    var onRender   = typeof opts.onRender === 'function' ? opts.onRender : function () {};
    var emptyNode  = typeof opts.emptyNode === 'function' ? opts.emptyNode : null;

    var FIRST     = opts.firstBatch || 24;
    var PAGE      = opts.pageSize   || 60;
    var AUTO      = opts.auto !== false;
    var AUTO_MAX  = opts.autoBatches || 8;
    var MAX_ITEMS = opts.maxItems || 750;
    var EAGER     = opts.eagerCount || 8;

    var view = [];
    var shown = 0;
    var autoUsed = 0;
    var busy = false;
    var engine = null;
    // How many to draw on the FIRST render when the shopper is coming back to
    // this grid. Consumed once. Without it the page is a fraction of the
    // height it was and there is nowhere to scroll to.
    var restoreTo = 0;

    function render(isAuto) {
      if (busy) return;
      var size = shown === 0 ? FIRST : PAGE;
      if (shown === 0 && restoreTo > size) {
        // Not a network call — the set is already in memory, so drawing the
        // shopper's whole previous run costs one longer loop and no spinner.
        size = Math.min(restoreTo, view.length);
        restoreTo = 0;
      }
      var slice = view.slice(shown, shown + size);
      if (!slice.length) return;

      busy = true;
      var frag = document.createDocumentFragment();
      var base = shown;
      slice.forEach(function (item, i) {
        var node = renderCard(item, base + i, base === 0 && i < EAGER);
        if (node) frag.appendChild(node);
      });
      grid.appendChild(frag);
      shown += slice.length;
      if (isAuto) autoUsed++;

      var done = shown >= view.length;
      var autoSpent = !AUTO || autoUsed >= AUTO_MAX;
      if (moreBtn)  moreBtn.hidden  = done || !autoSpent;
      if (sentinel) sentinel.hidden = done || autoSpent;
      onRender(shown, view.length);
      busy = false;
    }

    /* Rebuilt whenever the list changes. A filter can empty a list the engine
       had already given up on, and a stopped engine stays stopped by design,
       so recreating it is the honest way to get a working one back. */
    function makeEngine() {
      if (engine) { engine.destroy(); engine = null; }
      if (!AUTO || !sentinel || !global.ADGInfinite) return;
      engine = global.ADGInfinite.create({
        sentinel: sentinel,
        lookahead: opts.lookahead || 800,
        progress: function () { return shown; },
        canLoad: function () {
          if (autoUsed >= AUTO_MAX || shown >= view.length) return false;
          if (shown >= MAX_ITEMS) {
            if (moreBtn) moreBtn.hidden = false;
            sentinel.hidden = true;
            return false;
          }
          return true;
        },
        load: function () { render(true); },
        onStop: function () { if (moreBtn && shown < view.length) moreBtn.hidden = false; }
      });
      if (!engine && moreBtn) { moreBtn.hidden = false; sentinel.hidden = true; }
    }

    var api = {
      /* The whole filtered, sorted set. Replaces whatever was on show and
         draws the first batch. Callers filter; this only pages. */
      setItems: function (items, keepScroll) {
        view = items || [];
        shown = 0;
        autoUsed = 0;
        grid.innerHTML = '';

        if (!view.length) {
          if (emptyNode) grid.appendChild(emptyNode());
          if (moreBtn) moreBtn.hidden = true;
          if (sentinel) sentinel.hidden = true;
          if (engine) { engine.destroy(); engine = null; }
          onRender(0, 0);
          return;
        }

        // Only a first load may claim a return — a filter change is a new
        // set, and putting the shopper halfway down a different list would
        // be worse than starting at the top.
        if (!keepScroll) restoreTo = 0;
        render(false);
        makeEngine();
        if (engine) engine.check();
      },

      /* Coming back from a product: how many cards there were, and where the
         shopper was. Call BEFORE setItems. Returns the y to restore, or 0. */
      claimReturn: function () {
        if (!global.ADGScrollMemory) return 0;
        var back = global.ADGScrollMemory.recall();
        if (!back || !back.n) return 0;
        restoreTo = back.n;
        return back.y || 0;
      },

      /* A card is taking the shopper away. Record the position AND the count,
         because the scroll alone is meaningless against a grid that starts
         at one batch — and WHERE the card is taking them (`to`), because a
         return only counts when clicked from that page. See the mega-menu
         note in adg-scroll-memory.js. */
      remember: function (to) {
        if (!global.ADGScrollMemory) return;
        var extra = { n: shown };
        if (to) extra.to = to;
        global.ADGScrollMemory.remember(extra);
      },

      more: function () { render(false); },
      shown: function () { return shown; },
      total: function () { return view.length; },
      items: function () { return view; },
      destroy: function () { if (engine) { engine.destroy(); engine = null; } }
    };
    return api;
  }

  global.ADGGridCore = { create: create };
})(window);
